import { useEffect, useState, useRef } from "react";
import {
  CalendarIcon,
  ImageIcon,
  Loader2Icon,
  PencilIcon,
  XIcon,
} from "lucide-react";
import Calendar from "./mock-calendar";
import MainLayout from "../../../components/layout/MainLayout";
import { projectCreateService } from "../../../../Services/projectService";
import { setProjectId } from "../../../../Redux/Slice/authSlice";
import { useDispatch, useSelector } from "react-redux";
import { t } from "../../../../utils/translate";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import ImageUpload from "../../../../components/FileUpload";

const styles = {
  color: "red",
};

export default function ProjectForm() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loggedInUser } = useSelector((state) => state.auth);
  const roleOfUser = useSelector((state) => state?.auth?.loggedInUser?.type);
  const formRef = useRef(null); // Reference to form to prevent double submission

  const [isNameEdit, setIsNameEdit] = useState(false);
  const [isNumberEdit, setIsNumberEdit] = useState(false);
  const [buttonLoading, setButtonLoading] = useState(false);
  const [backgroundImage, setBackgroundImage] = useState("");
  const [eventsData, setEventsData] = useState([]);
  const [showCalendar, setShowCalendar] = useState(false);
  const [scaffoldStatus, setScaffoldStatus] = useState("inactive");
  const [formData, setFormData] = useState({
    projectName: "",
    projectNumber: "",
    company_name: "",
    company_organization_number: "",
    company_email: "",
    company_phone_number: "",
    comapny_invoice_address: "",
    company_contact_person: "",
    other_company_details: "",
    project_contact_person: "",
    project_phone_number: "",
    project_email: "",
    project_address: "",
    other_project_details: "",
  });
  const [errors, setErrors] = useState({});

  // Flag to track if form submission is in progress
  const isSubmitting = useRef(false);

  function generateRandomString() {
    const alphabet = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const numbers = "0123456789";

    let randomString = "";
    for (let i = 0; i < 3; i++) {
      const randomIndex = Math.floor(Math.random() * alphabet.length);
      randomString += alphabet[randomIndex];
    }

    for (let i = 0; i < 4; i++) {
      const randomIndex = Math.floor(Math.random() * numbers.length);
      randomString += numbers[randomIndex];
    }

    return randomString;
  }

  // Generate project number only once when component mounts
  useEffect(() => {
    if (!formData.projectNumber) {
      const projectNumber = generateRandomString();
      setFormData((prev) => ({ ...prev, projectNumber }));
    }
  }, []);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.projectName) {
      newErrors.projectName = "Project name is required";
    }

    if (
      formData.company_email &&
      !/\S+@\S+\.\S+/.test(formData.company_email)
    ) {
      newErrors.company_email = "Please enter a valid email";
    }

    if (
      formData.project_email &&
      !/\S+@\S+\.\S+/.test(formData.project_email)
    ) {
      newErrors.project_email = "Please enter a valid email";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleDeleteDocument = () => {
    setBackgroundImage("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting.current || buttonLoading) return;

    if (!validateForm()) return;

    try {
      // Set flags to prevent double submission
      isSubmitting.current = true;
      setButtonLoading(true);

      const payload = {
        projectName: formData?.projectName,
        companyDetails: {
          company_name: formData?.company_name,
          company_organization_number: formData?.company_organization_number,
          company_phone_number: formData?.company_phone_number,
          company_email: formData?.company_email,
          comapny_invoice_address: formData?.comapny_invoice_address,
          company_contact_person: formData?.company_contact_person,
          other_company_details: formData?.other_company_details,
        },
        projectDetails: {
          project_contact_person: formData?.project_contact_person,
          project_phone_number: formData?.project_phone_number,
          project_email: formData?.project_email,
          project_address: formData?.project_address,
          other_project_details: formData?.other_project_details,
        },
        Calendar: eventsData,
        projectNumber: formData?.projectNumber?.toUpperCase(),
        userId: loggedInUser._id,
        projectBackgroundImage: backgroundImage || "",
        notificationToAdminCreate: roleOfUser === 0 ? false : true,
        status: scaffoldStatus,
      };

      const projectCreation = await projectCreateService(payload);

      if (projectCreation.status === "success") {
        const projectId = projectCreation?.project?._id;
        dispatch(setProjectId(projectId));
        toast.success(t("projectCreatedSuccessfully"));
        navigate(`/admin/projects`);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to create project");
    } finally {
      setButtonLoading(false);
      isSubmitting.current = false;
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleImageUpload = (imgUrl, fileTagName) => {
    setBackgroundImage(imgUrl);
  };

  const handleSelect = ({ start, end }) => {
    const title = window.prompt("Add notes");
    if (title) {
      setEventsData((prev) => [...prev, { start, end, title }]);
    }
  };

  const handleEventClick = (event) => {
    const newTitle = window.prompt("Edit Event name", event.title);
    if (newTitle !== null) {
      setEventsData((prev) =>
        prev.map((ev) => {
          if (ev === event) {
            return { ...ev, title: newTitle };
          }
          return ev;
        })
      );
    }
  };

  console.log("backgroundImage", backgroundImage);

  return (
    <MainLayout title={"Create New Project"}>
      <div className="bg-gray-50 min-h-screen">
        <form
          ref={formRef}
          onSubmit={handleSubmit}
          className="max-w-7xl mx-auto px-4 py-8"
        >
          {/* Header Section */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-6">
              {/* Project Number */}
              <div className="flex items-center">
                <div className="flex items-center bg-gray-50 border border-gray-200 rounded-lg px-4 py-2">
                  <span className="text-gray-600 font-medium mr-2">
                    Project Number
                  </span>
                  <input
                    name="projectNumber"
                    value={formData.projectNumber}
                    onChange={handleChange}
                    readOnly={!isNumberEdit}
                    className="bg-transparent font-semibold text-blue-600 focus:outline-none w-28"
                  />
                  <button
                    type="button"
                    onClick={() => setIsNumberEdit(!isNumberEdit)}
                    className="ml-2 text-gray-400 hover:text-gray-600"
                  >
                    <PencilIcon size={16} />
                  </button>
                </div>
              </div>

              {/* Project Image Upload */}
              <div className="flex items-center">
                <div className="flex justify-center items-center gap-[1rem] bg-[#0072bb] font-[600] text-[#fff] sm:text-[16px] rounded-md text-nowrap">
                  {backgroundImage ? (
                    <ImageUpload
                      onImageUpload={handleImageUpload}
                      documentFile={"projectBackgroundImage"}
                      handleDeleteDocument={handleDeleteDocument}
                      status={true}
                      editedImage={backgroundImage}
                    />
                  ) : (
                    <ImageUpload
                      onImageUpload={handleImageUpload}
                      documentFile={"projectBackgroundImage"}
                      status={false}
                    />
                  )}
                </div>
                {errors?.projectBackgroundImage && (
                  <span style={styles}>
                    {errors?.projectBackgroundImage?.message}
                  </span>
                )}
                {backgroundImage && (
                  <button
                    type="button"
                    onClick={() => setBackgroundImage("")}
                    className="ml-2 bg-gray-100 hover:bg-gray-200 p-2 rounded-full"
                  >
                    <XIcon size={16} className="text-gray-600" />
                  </button>
                )}
              </div>
            </div>

            {/* Project Name */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Project Name
              </label>
              <input
                name="projectName"
                value={formData.projectName}
                onChange={handleChange}
                placeholder="Enter project name"
                className={`w-full px-4 py-3 text-lg font-medium text-blue-600 border ${
                  errors.projectName ? "border-red-500" : "border-gray-300"
                } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors`}
              />
              {errors.projectName && (
                <p className="mt-1 text-sm text-red-600">
                  {errors.projectName}
                </p>
              )}
            </div>

            {/* Background Image Preview */}
            {backgroundImage && (
              <div className="mt-6 relative">
                <img
                  src={backgroundImage || "/placeholder.svg"}
                  alt="Project background"
                  className="w-full h-40 object-cover rounded-lg"
                />
              </div>
            )}
          </div>

          {/* Main Form Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8">
            {/* Company Details Section */}
            <div className="mb-8">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  Company Details
                </h2>

                {/* Status Selection */}
                <div className="flex items-center gap-4 mt-4 md:mt-0">
                  <div className="flex items-center gap-2">
                    <input
                      id="active"
                      type="radio"
                      name="status"
                      checked={scaffoldStatus === "active"}
                      onChange={() => setScaffoldStatus("active")}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="active"
                      className="text-sm font-medium text-gray-700"
                    >
                      Active
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="inactive"
                      type="radio"
                      name="status"
                      checked={scaffoldStatus === "inactive"}
                      onChange={() => setScaffoldStatus("inactive")}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="inactive"
                      className="text-sm font-medium text-gray-700"
                    >
                      Inactive
                    </label>
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      id="completed"
                      type="radio"
                      name="status"
                      checked={scaffoldStatus === "completed"}
                      onChange={() => setScaffoldStatus("completed")}
                      className="w-4 h-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                    />
                    <label
                      htmlFor="completed"
                      className="text-sm font-medium text-gray-700"
                    >
                      Completed
                    </label>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <input
                    name="company_name"
                    value={formData.company_name}
                    onChange={handleChange}
                    placeholder="Enter company name"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Organization Number
                  </label>
                  <input
                    name="company_organization_number"
                    value={formData.company_organization_number}
                    onChange={handleChange}
                    placeholder="Enter organization number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Invoice Address
                  </label>
                  <input
                    name="comapny_invoice_address"
                    value={formData.comapny_invoice_address}
                    onChange={handleChange}
                    placeholder="Enter invoice address"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person
                  </label>
                  <input
                    name="company_contact_person"
                    value={formData.company_contact_person}
                    onChange={handleChange}
                    placeholder="Enter contact person"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    name="company_email"
                    value={formData.company_email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    className={`w-full px-4 py-2 border ${
                      errors.company_email
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  {errors.company_email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.company_email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    name="company_phone_number"
                    value={formData.company_phone_number}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Other Company Details
                  </label>
                  <textarea
                    name="other_company_details"
                    value={formData.other_company_details}
                    onChange={handleChange}
                    placeholder="Enter other company details"
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Project Details Section */}
            <div>
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-gray-800">
                  Project Details
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Contact Person
                  </label>
                  <input
                    name="project_contact_person"
                    value={formData.project_contact_person}
                    onChange={handleChange}
                    placeholder="Enter contact person"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number
                  </label>
                  <input
                    name="project_phone_number"
                    value={formData.project_phone_number}
                    onChange={handleChange}
                    placeholder="Enter phone number"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    name="project_email"
                    value={formData.project_email}
                    onChange={handleChange}
                    placeholder="Enter email address"
                    className={`w-full px-4 py-2 border ${
                      errors.project_email
                        ? "border-red-500"
                        : "border-gray-300"
                    } rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500`}
                  />
                  {errors.project_email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.project_email}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Address
                  </label>
                  <input
                    name="project_address"
                    value={formData.project_address}
                    onChange={handleChange}
                    placeholder="Enter address"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Other Project Details
                  </label>
                  <textarea
                    name="other_project_details"
                    value={formData.other_project_details}
                    onChange={handleChange}
                    placeholder="Enter other project details"
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  ></textarea>
                </div>

                <div className="flex items-center">
                  <button
                    type="button"
                    onClick={() => setShowCalendar(!showCalendar)}
                    className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    <CalendarIcon size={18} />
                    <span className="font-medium">Open Calendar</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Calendar Modal */}
          {showCalendar && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-auto">
                <div className="flex justify-between items-center p-4 border-b">
                  <h3 className="text-lg font-semibold">Project Calendar</h3>
                  <button
                    type="button"
                    onClick={() => setShowCalendar(false)}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <XIcon size={20} />
                  </button>
                </div>
                {/* Import and use the Calendar component */}
                <div className="p-4">
                  <Calendar
                    handleSelect={handleSelect}
                    eventsData={eventsData}
                    handleEventClick={handleEventClick}
                  />
                  <div className="mt-4 text-center">
                    <button
                      type="button"
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
                      onClick={() => {
                        const now = new Date();
                        const later = new Date(now.getTime() + 60 * 60 * 1000);
                        handleSelect({ start: now, end: later });
                      }}
                    >
                      Add Sample Event
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-center">
            <button
              type="submit"
              disabled={buttonLoading || isSubmitting.current}
              className={`bg-blue-600 ${
                buttonLoading ? "opacity-70" : "hover:bg-blue-700"
              } text-white px-8 py-3 rounded-lg font-medium text-lg transition-colors flex items-center gap-2`}
            >
              {buttonLoading ? (
                <>
                  <Loader2Icon size={20} className="animate-spin" />
                  <span>Creating Project...</span>
                </>
              ) : (
                <span>Create Project</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </MainLayout>
  );
}
